// A minimal client-side router (react-router-dom wasn't available to
// install in the build sandbox - see README). Supports plain paths and
// ":param" segments, which is all this app needs.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const RouterContext = createContext(null);

export function RouterProvider({ children }) {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to) => {
    const targetPathname = to.split("?")[0].split("#")[0];
    if (to === window.location.pathname + window.location.search) return;
    window.history.pushState({}, "", to);
    setPath(targetPathname);
    window.scrollTo(0, 0);
  }, []);

  return <RouterContext.Provider value={{ path, navigate }}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  return useContext(RouterContext);
}

export function Link({ to, className, children, onClick }) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

function matchPath(pattern, path) {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = path.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i];
    if (pp.startsWith(":")) {
      params[pp.slice(1)] = decodeURIComponent(pathParts[i]);
    } else if (pp !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

/**
 * routes: array of { path, render: (params) => JSX }
 * Renders the first matching route, or `notFound` if given.
 */
export function Switch({ routes, notFound }) {
  const { path } = useRouter();
  for (const route of routes) {
    const params = matchPath(route.path, path);
    if (params) return route.render(params);
  }
  return notFound || null;
}
